import { useTranslation } from "react-i18next";
import { URLS } from "../../../../services/urls";
import { useCallback, useEffect, useRef, useState } from "react";
import { API } from "../../../../services/axios";
import { useSelector } from "react-redux";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useForm } from "react-hook-form";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { confirmDeletion } from "../../../../components/dletion";
import CertificationCard from "./components/CertificationCard";
import { ErrorMessage } from "@hookform/error-message";

const ManageCertification = ({ handleNext, handlePrev }) => {
  const { t }      = useTranslation();
  const { action } = useParams();
  const { auth }   = useSelector((state) => state.auth);

  const [certifications, setCertifications] = useState([]);
  const [fetchLoading, setFetchLoading]     = useState(false);
  const [toEdit, setToEdit]                 = useState(null);
  const [loading, setLoading]               = useState(false);
  const [description, setDescription]       = useState("");
  const [file, setFile]                     = useState(null);

  const { register, handleSubmit, reset, formState: { errors } } = useForm({});
  const formRef = useRef(null);

  // ─── Fetch certifications, filter client-side by profile ─────────────────
  const loadCertifications = useCallback(() => {
    if (!action || action === "create") return;
    setFetchLoading(true);
    API.get(URLS.USERS.PROFILE.details(action))
      .then(({ data }) => {
        setCertifications(data.certifications || []);
        setFetchLoading(false);
      })
      .catch(() => setFetchLoading(false));
  }, [action]);

  useEffect(() => {
    loadCertifications();
  }, [loadCertifications]);

  // ─── File handlers ─────────────────────────────────────────────────────────
  const handleChangeCV = (e) => setFile(e.target.files[0]);
  const handleDeleteCV = (e) => { e.preventDefault(); setFile(null); };

  // ─── Edit ──────────────────────────────────────────────────────────────────
  const handleChangeCertification = (cert) => {
    setToEdit(cert);
    reset({ designation: cert.designation, date: cert.date });
    setFile(cert.file ? { name: cert.file } : null);
    setDescription(cert.description || "");
    formRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // ─── Delete ────────────────────────────────────────────────────────────────
  const handleDeleteCertification = (cert) => {
    const onSuccess = () => {
      setCertifications((prev) => prev.filter((c) => c.id !== cert.id));
    };
    confirmDeletion(URLS.USERS.PROFILE.CERTIFICATION.update(cert.id), t, onSuccess);
  };

  // ─── Submit ────────────────────────────────────────────────────────────────
  const onSubmit = (data) => {
    setLoading(true);
    const fd = new FormData();
    fd.append("designation",  data.designation);
    fd.append("date",         data.date);
    fd.append("profile",      action);
    fd.append("description",  description);
    if (file?.type) fd.append("file", file);

    if (!toEdit) {
      API.post(URLS.USERS.PROFILE.CERTIFICATION.create, fd)
        .then(({ data: resp }) => {
          setLoading(false);
          reset();
          setDescription("");
          setFile(null);
          toast.success(t("textCertifAdded"));
          setCertifications((prev) => [resp, ...prev]);
        })
        .catch((err) => {
          toast.error(
            err?.response?.data?.detail ??
            err?.response?.data?.messagerror ??
            t("textCertifAddingError")
          );
          setLoading(false);
        });
    } else {
      API.patch(URLS.USERS.PROFILE.CERTIFICATION.update(toEdit.id), fd)
        .then(({ data: resp }) => {
          setLoading(false);
          toast.success(t("educationEdited"));
          setCertifications((prev) =>
            prev.map((c) => (c.id === toEdit.id ? resp : c))
          );
          setToEdit(null);
          reset();
          setDescription("");
          setFile(null);
        })
        .catch((err) => {
          toast.error(err?.response?.data?.detail);
          setLoading(false);
        });
    }
  };

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="panel-white mb-30" id="certifications">
      <div className="box-padding">
        <h5 className="icon-edu">{t("textCertifications")}</h5>
        <div className="row mt-30">
          <div className="col-lg-9">
            <form onSubmit={handleSubmit(onSubmit)} ref={formRef}>
              <div className="row">
                {/* Name */}
                <div className="col-lg-6 col-md-6">
                  <div className="form-group mb-30">
                    <label className="font-sm color-text-mutted mb-10">
                      {t("textCertifName")} <span className="text-primary">*</span>
                    </label>
                    <input
                      {...register("designation", { required: t("textFormFieldRequired") })}
                      className="form-control"
                      type="text"
                      placeholder={t("textCertifName")}
                    />
                    <ErrorMessage errors={errors} name="designation"
                      render={({ message }) => <span className="text-primary">{message}</span>}
                    />
                  </div>
                </div>

                {/* Date */}
                <div className="col-lg-6 col-md-6">
                  <div className="form-group mb-30">
                    <label className="font-sm color-text-mutted mb-10">
                      {t("textDateObtained")} <span className="text-primary">*</span>
                    </label>
                    <input
                      {...register("date", { required: t("textFormFieldRequired") })}
                      className="form-control"
                      type="date"
                    />
                    <ErrorMessage errors={errors} name="date"
                      render={({ message }) => <span className="text-primary">{message}</span>}
                    />
                  </div>
                </div>

                {/* File upload */}
                <div className="col-lg-6 col-md-6">
                  <div className="form-group mb-30">
                    <div className="box-upload">
                      <div className="add-file-upload">
                        <input className="fileupload" type="file" id="uploadCertif" onChange={handleChangeCV} />
                      </div>
                      <label htmlFor="uploadCertif" className="btn btn-default">
                        {t("uploadFile")}
                      </label>
                    </div>
                  </div>
                </div>
                <div className="col-lg-6 col-md-6">
                  {file && (
                    <div className="form-group mb-30 box-file-uploaded">
                      <span>{file.name}</span><br />
                      <Link className="btn btn-delete" to="#" onClick={handleDeleteCV}>
                        {t("buttonDelete")}
                      </Link>
                    </div>
                  )}
                </div>

                {/* Description */}
                <div className="col-lg-12">
                  <div className="form-group mb-30">
                    <label className="font-sm color-text-mutted mb-10">{t("textDescription")}</label>
                    <ReactQuill
                      theme="snow"
                      value={description}
                      onChange={setDescription}
                      style={{ height: "180px", marginBottom: "50px" }}
                    />
                  </div>
                </div>

                {/* Submit */}
                <div className="col-lg-12">
                  <div className="form-group mt-10 d-flex">
                    <button type="submit" className="btn btn-default btn-brand icon-tick ml-10">
                      {!toEdit ? t("textAddCertification") : t("textEditCertification")}
                      {loading && <span className="spinner-border spinner-border-sm ml-10" role="status" />}
                    </button>
                    {toEdit && (
                      <button
                        type="button"
                        className="btn btn-secondary ml-10"
                        onClick={() => { setToEdit(null); reset(); setDescription(""); setFile(null); }}
                      >
                        {t("deleteActionCancel")}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </form>

            {/* List */}
            {fetchLoading ? (
              <div className="d-flex mt-30">
                <div className="spinner-border m-auto spinner-border-sm" role="status" />
              </div>
            ) : (
              <div className="box-timeline mt-50">
                {certifications.map((cert) => (
                  <CertificationCard
                    key={cert.id}
                    handleChange={handleChangeCertification}
                    handleDelete={handleDeleteCertification}
                    certification={cert}
                  />
                ))}
              </div>
            )}

            {/* Navigation */}
            <div className="col-lg-12 mt-3">
              <div className="form-group mt-10 d-flex">
                <button type="button" onClick={handlePrev} className="btn btn-secondary btn-brand line-height-24">
                  {t("textPrevious")}
                </button>
                <button type="button" onClick={handleNext} className="btn btn-default btn-brand ml-auto">
                  {t("textNext")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageCertification;
